import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { 
  ArrowLeft, BookOpen, Users, MessageCircle, AlertTriangle, 
  Search, Star, Clock, BookMarked, Cross, Send, Lightbulb, GraduationCap
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PhilosopherLibraryPage = ({ user }) => {
  const navigate = useNavigate();
  const { philosopherId } = useParams();
  const [philosophers, setPhilosophers] = useState([]);
  const [selectedPhilosopher, setSelectedPhilosopher] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filter, setFilter] = useState("all");
  
  // Chat state
  const [chatMessage, setChatMessage] = useState("");
  const [chatHistory, setChatHistory] = useState([]);
  const [chatLoading, setChatLoading] = useState(false);

  useEffect(() => {
    fetchPhilosophers();
  }, []);

  useEffect(() => {
    if (philosopherId) {
      fetchPhilosopherProfile(philosopherId);
    }
  }, [philosopherId]);

  const fetchPhilosophers = async () => {
    try {
      const response = await axios.get(`${API}/philosophers`);
      setPhilosophers(response.data);
    } catch (error) {
      console.error("Error fetching philosophers:", error);
      toast.error("Erreur lors du chargement des philosophes");
    } finally {
      setLoading(false);
    }
  };

  const fetchPhilosopherProfile = async (id) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/philosophers/${id}/profile`);
      setProfile(response.data);
      setSelectedPhilosopher(response.data);
      setChatHistory([]);
    } catch (error) {
      console.error("Error fetching profile:", error);
      toast.error("Erreur lors du chargement du profil");
    } finally {
      setLoading(false);
    }
  };

  const handleSendChat = async () => {
    if (!chatMessage.trim() || !selectedPhilosopher) return;
    
    const userMsg = chatMessage;
    setChatMessage("");
    setChatHistory(prev => [...prev, { role: "user", content: userMsg }]);
    setChatLoading(true);
    
    try {
      const response = await axios.post(`${API}/chat/private`, {
        student_id: user?.id || "guest",
        philosopher_id: selectedPhilosopher.id,
        message: userMsg
      });
      
      setChatHistory(prev => [...prev, { 
        role: "philosopher", 
        content: response.data.response,
        philosopher: selectedPhilosopher.name
      }]);
    } catch (error) {
      console.error("Chat error:", error);
      toast.error("Erreur de communication avec le philosophe");
    } finally {
      setChatLoading(false);
    }
  };

  const filteredPhilosophers = philosophers.filter(p => {
    const matchesSearch = p.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filter === "all" || p.tradition === filter;
    return matchesSearch && matchesFilter;
  });

  const getTraditionBadge = (tradition) => {
    switch (tradition) {
      case "chretien":
        return <Badge className="bg-[#D4AF37] text-[#0F172A]"><Cross className="w-3 h-3 mr-1" />Chretien</Badge>;
      case "fondateur":
        return <Badge className="bg-[#22C55E]"><Star className="w-3 h-3 mr-1" />Fondateur</Badge>;
      case "moderne":
        return <Badge variant="outline" className="border-[#64748B]">Moderne</Badge>;
      default:
        return <Badge variant="outline">Classique</Badge>;
    }
  };

  const getProgramBadge = (philosopher) => {
    if (!philosopher.program_label) return null;
    
    const colorMap = {
      green: "bg-green-500 text-white",
      blue: "bg-blue-500 text-white", 
      teal: "bg-teal-500 text-white",
      gold: "bg-[#D4AF37] text-[#0F172A]",
      gray: "bg-gray-200 text-gray-600"
    };
    
    const badgeClass = colorMap[philosopher.program_color] || colorMap.gray;
    
    return (
      <Badge className={`${badgeClass} text-xs`}>
        <BookOpen className="w-3 h-3 mr-1" />
        {philosopher.program_label}
      </Badge>
    );
  };

  // Philosopher list view
  if (!philosopherId) {
    return (
      <div className="min-h-screen bg-[#F8F9FA]" data-testid="philosopher-library-page">
        {/* Header */}
        <nav className="navbar px-4 md:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/dashboard")}
              className="text-white hover:bg-white/10"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Retour
            </Button>
            <span className="navbar-logo hidden sm:block">SophIA</span>
            <Badge className="ml-2 bg-[#D4AF37] text-[#0F172A]">Bibliotheque</Badge>
          </div>
        </nav>

        <main className="px-4 md:px-8 py-6 max-w-7xl mx-auto page-enter">
          <div className="mb-6">
            <h1 className="text-2xl md:text-3xl text-[#1A365D] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
              Bibliothèque des Philosophes
            </h1>
            <p className="text-[#64748B]">
              Découvrez les grands penseurs, leurs idées, leurs controverses... et discutez avec eux !
            </p>
          </div>

          {/* Search and Filters */}
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
              <Input
                placeholder="Rechercher un philosophe..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="philosopher-search"
              />
            </div>
            <div className="flex gap-2">
              <Button
                variant={filter === "all" ? "default" : "outline"}
                onClick={() => setFilter("all")}
                className={filter === "all" ? "bg-[#1A365D]" : ""}
              >
                Tous ({philosophers.length})
              </Button>
              <Button
                variant={filter === "chretien" ? "default" : "outline"}
                onClick={() => setFilter("chretien")}
                className={filter === "chretien" ? "bg-[#D4AF37] text-[#0F172A]" : ""}
              >
                <Cross className="w-4 h-4 mr-1" />
                Chretiens
              </Button>
              <Button
                variant={filter === "moderne" ? "default" : "outline"}
                onClick={() => setFilter("moderne")}
              >
                Modernes
              </Button>
            </div>
          </div>

          {/* Philosopher Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredPhilosophers.map((philosopher) => (
              <Card 
                key={philosopher.id} 
                className="card-sjh cursor-pointer hover:shadow-lg transition-shadow"
                onClick={() => navigate(`/library/${philosopher.id}`)}
                data-testid={`philosopher-card-${philosopher.id}`}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-3">
                    {philosopher.avatar ? (
                      <img 
                        src={philosopher.avatar} 
                        alt={philosopher.name}
                        className="w-16 h-16 rounded-full object-cover border-2 border-[#D4AF37]"
                      />
                    ) : (
                      <div className="w-16 h-16 rounded-full bg-[#1A365D] flex items-center justify-center text-white text-xl">
                        {philosopher.name.charAt(0)}
                      </div>
                    )}
                    <div>
                      <CardTitle className="text-lg text-[#1A365D]">{philosopher.name}</CardTitle>
                      <CardDescription className="text-xs">{philosopher.era}</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-1 mb-2">
                    {getTraditionBadge(philosopher.tradition)}
                    {getProgramBadge(philosopher)}
                  </div>
                  <p className="text-sm text-[#64748B] line-clamp-2">
                    {philosopher.personality}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </main>
      </div>
    );
  }

  // Philosopher detail view
  return (
    <div className="min-h-screen bg-[#F8F9FA]" data-testid="philosopher-detail-page">
      {/* Header */}
      <nav className="navbar px-4 md:px-8 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/library")}
            className="text-white hover:bg-white/10"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Bibliotheque
          </Button>
          <span className="navbar-logo hidden sm:block">SophIA</span>
        </div>
      </nav>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-pulse text-[#64748B]">Chargement...</div>
        </div>
      ) : profile && (
        <main className="px-4 md:px-8 py-6 max-w-6xl mx-auto page-enter">
          {/* Profile Header */}
          <div className="flex flex-col md:flex-row gap-6 mb-8">
            <div className="flex-shrink-0">
              {profile.avatar ? (
                <img 
                  src={profile.avatar} 
                  alt={profile.name}
                  className="w-32 h-32 rounded-xl object-cover border-4 border-[#D4AF37] shadow-lg"
                />
              ) : (
                <div className="w-32 h-32 rounded-xl bg-[#1A365D] flex items-center justify-center text-white text-4xl">
                  {profile.name.charAt(0)}
                </div>
              )}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-3xl text-[#1A365D]" style={{ fontFamily: 'Playfair Display, serif' }}>
                  {profile.name}
                </h1>
                {getTraditionBadge(profile.tradition)}
              </div>
              <p className="text-[#64748B] mb-3">{profile.era}</p>
              <div className="flex flex-wrap gap-2">
                {profile.bac_notions?.map((notion, i) => (
                  <Badge key={i} variant="outline" className="bg-white">
                    <BookMarked className="w-3 h-3 mr-1" />
                    {notion}
                  </Badge>
                ))}
              </div>
            </div>
          </div>

          <Tabs defaultValue="bio" className="w-full">
            <TabsList className="grid w-full grid-cols-6 mb-6">
              <TabsTrigger value="bio" className="flex items-center gap-1">
                <BookOpen className="w-4 h-4" />
                <span className="hidden sm:inline">Bio</span>
              </TabsTrigger>
              <TabsTrigger value="oeuvres" className="flex items-center gap-1">
                <BookMarked className="w-4 h-4" />
                <span className="hidden sm:inline">Oeuvres</span>
              </TabsTrigger>
              <TabsTrigger value="themes" className="flex items-center gap-1">
                <GraduationCap className="w-4 h-4" />
                <span className="hidden sm:inline">Themes</span>
              </TabsTrigger>
              <TabsTrigger value="critique" className="flex items-center gap-1">
                <AlertTriangle className="w-4 h-4" />
                <span className="hidden sm:inline">Critique</span>
              </TabsTrigger>
              <TabsTrigger value="connexions" className="flex items-center gap-1">
                <Users className="w-4 h-4" />
                <span className="hidden sm:inline">Liens</span>
              </TabsTrigger>
              <TabsTrigger value="chat" className="flex items-center gap-1">
                <MessageCircle className="w-4 h-4" />
                <span className="hidden sm:inline">Chat</span>
              </TabsTrigger>
            </TabsList>

            <TabsContent value="bio">
              <Card className="card-sjh">
                <CardHeader>
                  <CardTitle className="text-[#1A365D]">Biographie</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-[#0F172A] whitespace-pre-line leading-relaxed">
                    {profile.full_bio}
                  </p>
                  
                  {profile.main_works?.length > 0 && (
                    <div className="mt-6">
                      <h3 className="font-semibold text-[#1A365D] mb-2">Oeuvres principales</h3>
                      <ul className="list-disc list-inside text-[#64748B]">
                        {profile.main_works.map((work, i) => (
                          <li key={i}>{work}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="oeuvres">
              <Card className="card-sjh">
                <CardHeader>
                  <CardTitle className="text-[#1A365D] flex items-center gap-2">
                    <BookMarked className="w-5 h-5 text-[#D4AF37]" />
                    Oeuvres et Analyses
                  </CardTitle>
                  <CardDescription>
                    Les grandes oeuvres de {profile.name} et ce qu'elles soutiennent
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {profile.works_detailed && Object.keys(profile.works_detailed).length > 0 ? (
                    <div className="space-y-6">
                      {Object.entries(profile.works_detailed).map(([title, work], i) => (
                        <div 
                          key={i} 
                          className="p-5 bg-gradient-to-br from-[#F8F9FA] via-white to-[#F0F9FF] rounded-xl border border-[#E2E8F0] shadow-sm"
                        >
                          <div className="flex items-start gap-4">
                            <div className="p-3 bg-[#1A365D] rounded-lg flex-shrink-0">
                              <BookOpen className="w-5 h-5 text-[#D4AF37]" />
                            </div>
                            <div className="flex-1 space-y-4">
                              <div>
                                <h4 className="text-xl font-semibold text-[#1A365D]" style={{ fontFamily: 'Playfair Display, serif' }}>
                                  {title}
                                </h4>
                                <Badge className="mt-1 bg-[#22C55E]/10 text-[#15803D] border border-[#22C55E]/30">
                                  {work.theme_principal}
                                </Badge>
                              </div>
                              
                              <div className="p-3 bg-white rounded-lg border-l-4 border-[#D4AF37]">
                                <p className="text-[#0F172A] leading-relaxed">
                                  {work.analyse}
                                </p>
                              </div>
                              
                              {work.theses_soutenues?.length > 0 && (
                                <div>
                                  <h5 className="font-semibold text-[#1A365D] mb-2 flex items-center gap-2">
                                    <Lightbulb className="w-4 h-4 text-[#D4AF37]" />
                                    Theses soutenues
                                  </h5>
                                  <ul className="space-y-2">
                                    {work.theses_soutenues.map((these, j) => (
                                      <li key={j} className="flex items-start gap-2 text-[#0F172A]">
                                        <Star className="w-4 h-4 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                                        <span>{these}</span>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                              
                              {work.citations_cles?.length > 0 && (
                                <div className="p-3 bg-[#FEF3C7] rounded-lg">
                                  <h5 className="font-semibold text-[#92400E] mb-2">Citations cles</h5>
                                  {work.citations_cles.map((citation, j) => (
                                    <p key={j} className="text-[#92400E] italic mb-1">
                                      "{citation}"
                                    </p>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-[#64748B]">
                      <BookMarked className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p>Analyses detaillees en cours d'elaboration</p>
                      {profile.main_works?.length > 0 && (
                        <div className="mt-4">
                          <p className="text-sm mb-2">Oeuvres principales :</p>
                          <ul className="text-sm">
                            {profile.main_works.map((work, i) => (
                              <li key={i}>{work}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="themes">
              <Card className="card-sjh">
                <CardHeader>
                  <CardTitle className="text-[#1A365D] flex items-center gap-2">
                    <GraduationCap className="w-5 h-5 text-[#D4AF37]" />
                    Positions sur les themes du Baccalaureat
                  </CardTitle>
                  <CardDescription>
                    Ce que pense {profile.name} des grandes notions du programme
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {profile.theme_positions && Object.keys(profile.theme_positions).length > 0 ? (
                    <div className="grid gap-4">
                      {Object.entries(profile.theme_positions).map(([theme, position], i) => (
                        <div 
                          key={i} 
                          className="p-4 bg-gradient-to-r from-[#F8F9FA] to-white rounded-lg border border-[#E2E8F0] hover:shadow-md transition-shadow"
                        >
                          <div className="flex items-start gap-3">
                            <div className="p-2 bg-[#1A365D] rounded-lg flex-shrink-0">
                              <Lightbulb className="w-4 h-4 text-[#D4AF37]" />
                            </div>
                            <div className="flex-1">
                              <h4 className="font-semibold text-[#1A365D] mb-2 flex items-center gap-2">
                                {theme}
                                <Badge variant="outline" className="text-xs bg-[#D4AF37]/10 text-[#92400E] border-[#D4AF37]">
                                  Programme Bac
                                </Badge>
                              </h4>
                              <p className="text-[#0F172A] leading-relaxed">
                                {position}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-[#64748B]">
                      <GraduationCap className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p>Positions en cours d'elaboration pour ce philosophe</p>
                    </div>
                  )}
                  
                  {profile.bac_notions?.length > 0 && (
                    <div className="mt-6 p-4 bg-[#F0F9FF] rounded-lg">
                      <h4 className="font-semibold text-[#1A365D] mb-2">
                        Notions cles associees a ce philosophe
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {profile.bac_notions.map((notion, i) => (
                          <Badge key={i} className="bg-[#1A365D] text-white">
                            <BookMarked className="w-3 h-3 mr-1" />
                            {notion}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="critique">
              <Card className="card-sjh">
                <CardHeader>
                  <CardTitle className="text-[#1A365D] flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-[#EAB308]" />
                    Analyse Critique
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {profile.controversies?.length > 0 && (
                    <div className="p-4 bg-[#FEF3C7] rounded-lg border border-[#F59E0B]">
                      <h3 className="font-semibold text-[#92400E] mb-2">Points de vigilance</h3>
                      <ul className="space-y-2">
                        {profile.controversies.map((c, i) => (
                          <li key={i} className="flex items-start gap-2 text-[#92400E]">
                            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                            {c}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  <div className="prose prose-slate max-w-none">
                    <p className="text-[#0F172A] whitespace-pre-line leading-relaxed">
                      {profile.critical_analysis}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="connexions">
              <Card className="card-sjh">
                <CardHeader>
                  <CardTitle className="text-[#1A365D]">Connexions philosophiques</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-4">
                    {profile.connections?.influenced_by && (
                      <div className="p-4 bg-[#F0F9FF] rounded-lg">
                        <h3 className="font-semibold text-[#1A365D] mb-2">Influences recues</h3>
                        <div className="flex flex-wrap gap-2">
                          {profile.connections.influenced_by.map((p, i) => (
                            <Badge key={i} variant="outline">{p}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {profile.connections?.influenced && (
                      <div className="p-4 bg-[#F0FDF4] rounded-lg">
                        <h3 className="font-semibold text-[#22C55E] mb-2">A influence</h3>
                        <div className="flex flex-wrap gap-2">
                          {profile.connections.influenced.map((p, i) => (
                            <Badge key={i} className="bg-[#22C55E]/20 text-[#22C55E]">{p}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {profile.connections?.christian_response && (
                      <div className="p-4 bg-[#FEF3C7] rounded-lg col-span-2">
                        <h3 className="font-semibold text-[#92400E] mb-2">
                          <Cross className="w-4 h-4 inline mr-1" />
                          Réponse chrétienne
                        </h3>
                        <p className="text-sm text-[#92400E] mb-2">
                          Ces philosophes chrétiens peuvent répondre et nuancer ses positions :
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {profile.connections.christian_response.map((p, i) => (
                            <Badge key={i} className="bg-[#D4AF37] text-[#0F172A]">{p}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {profile.debate_topics?.length > 0 && (
                    <div className="mt-6">
                      <h3 className="font-semibold text-[#1A365D] mb-2">Sujets de debat</h3>
                      <ul className="space-y-2">
                        {profile.debate_topics.map((topic, i) => (
                          <li key={i} className="flex items-center gap-2 text-[#64748B]">
                            <MessageCircle className="w-4 h-4 text-[#1A365D]" />
                            {topic}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="chat">
              <Card className="card-sjh">
                <CardHeader>
                  <CardTitle className="text-[#1A365D] flex items-center gap-2">
                    <MessageCircle className="w-5 h-5" />
                    Discuter avec {profile.name}
                  </CardTitle>
                  <CardDescription>
                    Posez vos questions directement au philosophe
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[300px] mb-4 p-4 bg-[#F8F9FA] rounded-lg">
                    {chatHistory.length === 0 ? (
                      <div className="text-center text-[#64748B] py-8">
                        <MessageCircle className="w-12 h-12 mx-auto mb-2 opacity-50" />
                        <p>Commencez la conversation avec {profile.name}</p>
                        <p className="text-sm mt-2">
                          Suggestions : "{profile.debate_topics?.[0]}"
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {chatHistory.map((msg, i) => (
                          <div 
                            key={i}
                            className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                          >
                            <div className={`max-w-[80%] p-3 rounded-lg ${
                              msg.role === "user" 
                                ? "bg-[#1A365D] text-white" 
                                : "bg-white border border-[#E2E8F0]"
                            }`}>
                              {msg.role === "philosopher" && (
                                <p className="text-xs text-[#D4AF37] font-semibold mb-1">
                                  {msg.philosopher}
                                </p>
                              )}
                              <p className="text-sm">{msg.content}</p>
                            </div>
                          </div>
                        ))}
                        {chatLoading && (
                          <div className="flex justify-start">
                            <div className="bg-white border border-[#E2E8F0] p-3 rounded-lg">
                              <p className="text-xs text-[#D4AF37] font-semibold mb-1">{profile.name}</p>
                              <p className="text-sm text-[#64748B] animate-pulse">Reflechit...</p>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </ScrollArea>
                  
                  <div className="flex gap-2">
                    <Input
                      placeholder="Posez votre question..."
                      value={chatMessage}
                      onChange={(e) => setChatMessage(e.target.value)}
                      onKeyPress={(e) => e.key === "Enter" && handleSendChat()}
                      disabled={chatLoading}
                      data-testid="philosopher-chat-input"
                    />
                    <Button 
                      onClick={handleSendChat}
                      disabled={chatLoading || !chatMessage.trim()}
                      className="bg-[#1A365D]"
                      data-testid="philosopher-chat-send"
                    >
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </main>
      )}
    </div>
  );
};

export default PhilosopherLibraryPage;
